import API_URL from 'src/api/apiConstants';
import { DATE_FORMAT, REPORT_FILTER_OPTIONS } from 'src/utils/constants';
import { getFormattedDate } from 'src/utils/getInitials';
import { customisedMileageReportFormConfig, reportFormConfig } from './reportFormModel';
import { sensorTransactionReportFormConfig } from '../sensorTransactions/sensorTransactionReportFormModel';
import { breakdownReportFormConfig } from '../breakdown/breakdownReportFormModel';
import { vehicleUtilizationReportFormConfig } from '../otherReports/vehicleUtilizationReportFormModel';
import { vehicleSummaryReportFormConfig } from '../otherReports/vehicleSummaryReportFormModel';
import { hsdVsAvgConsumptionReportFormConfig } from '../otherReports/hsdVsAvgConsumptionReportFormModel';
import { vehicleWiseFuelConsumptionReportFormConfig } from '../otherReports/vehicleWiseFuelConsumptionReportFormModel';
import { machineryDieselConsumptionReportFormConfig } from '../otherReports/machineryDieselConsumptionReportFormModel';

export const REPORTS = {
  vehicleSummary: 'vehicleSummaryReport',
  vehicleUtilization: 'vehicleUtilizationReport',
  sensorTransaction: 'sensorTransactionReport',
  vehicleGroupWise: 'vehicleGroupWiseReport',
  groupWiseComparison: 'groupWiseComparisonReport',
  breakdown: 'breakdownReport',
  milage: 'mileageReport',
  customisedMileage: 'customisedMileageReport',
  vehicleRecords: 'vehicleRecordsReport',
  hsdVsAvgConsumption: 'hsdVsAvgConsumptionReport',
  vehicleWiseFuelConsumption: 'vehicleWiseFuelConsumptionReport',
  machineryDieselConsumption: 'machineryDieselConsumptionReport'
};

// eslint-disable-next-line import/prefer-default-export
export const getReportConfig = (reportName) => {
  const defaultConfig = {
    filterOptions: {},
    downloadUrl: API_URL[reportName],
    dateConfig: { onlyDate: false, dateFormat: null },
    formConfig: reportFormConfig,
    showViewButton: true,
    showTable: true,
    tableVariant: 'generic'
  };
  let reportConfig = { ...defaultConfig };
  switch (reportName) {
    case REPORTS.vehicleSummary:
      reportConfig = {
        ...defaultConfig,
        filterOptions: { wailonVehicle: true },
        dateConfig: { onlyDate: true },
        formConfig: vehicleSummaryReportFormConfig
      };
      break;
    case REPORTS.vehicleUtilization:
      reportConfig = {
        ...defaultConfig,
        filterOptions: { assetType: true },
        dateConfig: { onlyDate: true, views: ['month', 'year'], openTo: 'month' },
        formConfig: vehicleUtilizationReportFormConfig
      };
      break;
    case REPORTS.sensorTransaction:
      reportConfig = {
        ...defaultConfig,
        filterOptions: { vehicle: true },
        dateConfig: { dateFormat: DATE_FORMAT.timeStamp, disableShiftPrefill: true },
        formConfig: sensorTransactionReportFormConfig
      };
      break;
    case REPORTS.vehicleGroupWise:
      reportConfig = {
        ...defaultConfig,
        filterOptions: { assetType: true },
        dateConfig: { dateFormat: DATE_FORMAT.timeStamp, disableShiftPrefill: true },
      };
      break;
    case REPORTS.groupWiseComparison:
      reportConfig = {
        ...defaultConfig,
        formConfig: vehicleUtilizationReportFormConfig
      };
      break;
    case REPORTS.breakdown:
      reportConfig = {
        ...defaultConfig,
        filterOptions: { vehicle: true, assetType: true },
        dateConfig: { dateFormat: DATE_FORMAT.timeStamp },
        formConfig: breakdownReportFormConfig
      };
      break;
    case REPORTS.milage:
      reportConfig = {
        ...defaultConfig,
        filterOptions: { ...REPORT_FILTER_OPTIONS, dispenser: false },
      };
      break;
    case REPORTS.customisedMileage:
      reportConfig = {
        ...defaultConfig,
        downloadUrl: API_URL.customisedMileageReport,
        filterOptions: { ...REPORT_FILTER_OPTIONS, dispenser: false },
        formConfig: customisedMileageReportFormConfig,
        dateConfig: {
          onlyDate: true,
          nativeInput: false,
          displayFormat: 'DD/MM/YYYY',
          disableShiftPrefill: true
        },
        tableVariant: 'customisedMileage'
      };
      break;
    case REPORTS.vehicleWiseFuelConsumption:
      reportConfig = {
        ...defaultConfig,
        filterOptions: {
          ...REPORT_FILTER_OPTIONS,
          dispenser: false,
          vehicle: false,
          vehicleGroup: false
        },
        formConfig: vehicleWiseFuelConsumptionReportFormConfig,
        dateConfig: {
          onlyDate: true,
          nativeInput: true,
          disableShiftPrefill: true
        },
        tableVariant: 'vehicleWiseFuelConsumption'
      };
      break;
    case REPORTS.vehicleRecords:
      reportConfig = {
        ...defaultConfig,
        dateConfig: { onlyDate: true },
        formConfig: breakdownReportFormConfig
      };
      break;
    case REPORTS.hsdVsAvgConsumption:
      reportConfig = {
        ...defaultConfig,
        dateConfig: {
          onlyDate: true,
          views: ['year', 'month'],
          openTo: 'month',
          nativeInput: false
        },
        formConfig: hsdVsAvgConsumptionReportFormConfig,
        showViewButton: false,
        showTable: false
      };
      break;
    case REPORTS.machineryDieselConsumption:
      reportConfig = {
        ...defaultConfig,
        downloadUrl: API_URL.machineryDieselConsumptionReport,
        filterOptions: {},
        dateConfig: {
          onlyDate: true,
          views: ['year', 'month'],
          openTo: 'month',
          nativeInput: false
        },
        formConfig: machineryDieselConsumptionReportFormConfig,
        showViewButton: false,
        showTable: false
      };
      break;
    default:
      break;
  }
  return reportConfig;
};

export const getReportQueryPayload = (payload) => {
  const {
    title, data, startTime, endTime
  } = payload;
  let formPayload = { ...data };
  if (title === REPORTS.vehicleSummary) {
    formPayload = {
      [startTime.name]: getFormattedDate(data[startTime.name], 'DD-MM-YYYY'),
      [endTime.name]: getFormattedDate(data[endTime.name], 'DD-MM-YYYY')
    };
  } else if (title === REPORTS.vehicleUtilization) {
    const formattedDate = getFormattedDate(formPayload[startTime.name], 'MMM,MM,YYYY');
    const dateArray = formattedDate.split(',');
    formPayload = {
      asset_type: data.asset_type, month: dateArray[1], year: dateArray[2], month_text: dateArray[0]
    };
    delete formPayload[startTime.name];
  } else if (title === REPORTS.groupWiseComparison) {
    formPayload = { date: getFormattedDate(formPayload[startTime.name], 'DD-MM-YYYY:HH-mm') };
    delete formPayload[startTime.name];
  } else if (title === REPORTS.hsdVsAvgConsumption) {
    formPayload = {
      month: getFormattedDate(formPayload[startTime.name], 'M'),
      year: getFormattedDate(formPayload[startTime.name], 'YYYY')
    };
  } else if (title === REPORTS.machineryDieselConsumption) {
    formPayload = {
      month: getFormattedDate(formPayload[startTime.name], 'M'),
      year: getFormattedDate(formPayload[startTime.name], 'YYYY')
    };
  }

  const keyList = Object.keys(formPayload);
  const filteredPayload = {};
  keyList.forEach((key) => {
    if (formPayload[key] && formPayload[key] !== 'all' && formPayload[key] !== 'NA') {
      filteredPayload[key] = formPayload[key];
    }
  });
  return filteredPayload;
};

export const getFormattedReportURL = (url, payload, isExport) => {
  const {
    title, data, pageNo, limit
  } = payload;
  const queryPayload = getReportQueryPayload(payload);
  const hasQueryParams = url.includes('?');
  const paginationPrefix = hasQueryParams ? '&' : '?';
  let reportUrl = isExport ? url : `${url}${paginationPrefix}page=${pageNo}&page_size=${limit}`;
  if (title === REPORTS.vehicleSummary) {
    reportUrl = url.replace('{vehicleId}', data.vehicle_id);
  }

  const queryList = Object.keys(queryPayload).map((key) => `${key}=${queryPayload[key]}`);
  let apiURL = reportUrl;
  if (queryList.length > 0) {
    const separator = reportUrl.includes('?') ? '&' : '?';
    apiURL = `${reportUrl}${separator}${queryList.join('&')}`;
  }

  return apiURL;
};
